import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { MCOnboardingTabs } from '../tabs/MCOnboardingTabs'
import { InviteMCButton } from '../invite-mc/InviteMCButton'
import { ManageTagsButton } from 'src/components/tags/ManageTagsButton'
import { useLocation } from 'react-router-dom'
import { MCOnboardingsTable } from '../mc-onboardings-table/MCOnboardingsTable'
import { useDispatch, useSelector } from 'react-redux'
import { getPartnerId } from 'src/selectors/user'
import { getSelectedMarket } from 'src/selectors/market'
import { FetchMCOnboardingsCounts } from 'src/constants/actionTypes'
import { IMCOnboardingsContext, MCOnboardingsContext } from './MCOnboardings.context'
import { MCStatus } from 'src/constants/mc/enums'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

const MCOnboardingsComponent = () => {
  const dispatch = useDispatch()
  const { state } = useLocation()

  const { hasPermission } = useCheckPermissions()

  const partnerId = useSelector(getPartnerId)
  const selectedMarket = useSelector(getSelectedMarket)

  const selectedTabRef = useRef<MCStatus | null>(null)

  const [selectedTab, setSelectedTab] = useState(MCStatus.Invited)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [tagsFilter, setTagsFilter] = useState<Array<string>>([])

  const handleSelectTab = useCallback((tab: MCStatus) => {
    selectedTabRef.current = tab
    setSelectedTab(tab)
  }, [])

  useEffect(() => {
    if (!state?.selectedTab || state?.selectedTab === selectedTabRef.current) {
      return
    }

    handleSelectTab(state.selectedTab)
  }, [state?.selectedTab])

  const fetchOnboardingCounts = useCallback(() => {
    if (!partnerId) {
      return
    }

    const params: any = { partnerId }

    if (selectedMarket?.id) {
      params.partnerMarketId = selectedMarket?.id
    }

    dispatch(FetchMCOnboardingsCounts.request(params))
  }, [partnerId, selectedMarket?.id])

  useEffect(() => {
    fetchOnboardingCounts()
  }, [fetchOnboardingCounts])

  const onboardingsContextValue = useMemo<IMCOnboardingsContext>(() => {
    return {
      searchQuery,
      setSearchQuery,

      tagsFilter,
      setTagsFilter,
    }
  }, [searchQuery, setSearchQuery, tagsFilter, setTagsFilter])

  return (
    <MCOnboardingsContext.Provider value={onboardingsContextValue}>
      <div>
        <div css={tw`flex flex-row items-center justify-between mb-6`}>
          <Typography variant="h2" css={tw`mb-2`}>
            Master Contractors Onboarding
          </Typography>
          <div css={tw`flex`}>
            <ManageTagsButton />
            {hasPermission(UserPermission.CustomerMasterContractorCreate) && <InviteMCButton />}
          </div>
        </div>
        <MCOnboardingTabs selectedTab={selectedTab} onClick={handleSelectTab} />
        {selectedTab === MCStatus.Invited && (
          <MCOnboardingsTable selectedStatus={MCStatus.Invited} />
        )}
      </div>
    </MCOnboardingsContext.Provider>
  )
}

export const MCOnboardings = memo(MCOnboardingsComponent)
